import { apiBase } from "../store/slices/API";

const toAssetIconOption = (value) => ({
  label: value?.split("/").pop()?.split(".")[0].replace("-", " ") || value,
  value,
  icon: value,
});

export const toAssetTypeOption = (type) => ({
  label: type.Label || type.Value,
  value: type.Value,
  icon: type.Icon || type.Value,
  isDefault: !!type.IsDefault,
});

const assetTypeOptionCache = new Map();
const assetTypeRequestCache = new Map();
const assetTypeResponseCache = new Map();
let assetTypeCacheVersion = 0;
const assetTypeCacheListeners = new Set();

export const subscribeAssetTypeOptionCache = (listener) => {
  assetTypeCacheListeners.add(listener);
  return () => {
    assetTypeCacheListeners.delete(listener);
  };
};

export const getAssetTypeOptionCacheVersion = () => assetTypeCacheVersion;

export const clearAssetTypeOptionCache = (values = []) => {
  assetTypeCacheVersion += 1;
  assetTypeResponseCache.clear();
  assetTypeRequestCache.clear();
  values.forEach((value) => assetTypeOptionCache.delete(value));
  if (!values.length) {
    assetTypeOptionCache.clear();
  }
  assetTypeCacheListeners.forEach((listener) => listener(assetTypeCacheVersion));
};

const cacheAssetTypeOptions = (options) => {
  options.forEach((option) => {
    if (option?.value) {
      assetTypeOptionCache.set(option.value, option);
    }
  });
};

export const formatAssetTypeValue = (label) =>
  label
    ?.trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "";

export const fetchAssetTypeOptions = async ({
  search = "",
  offset = 0,
  values = [],
} = {}) => {
  const normalizedValues = [...new Set(values.filter(Boolean))];
  const requestKey = JSON.stringify({ search, offset, values: normalizedValues });
  if (assetTypeResponseCache.has(requestKey)) {
    return assetTypeResponseCache.get(requestKey);
  }
  if (assetTypeRequestCache.has(requestKey)) {
    return assetTypeRequestCache.get(requestKey);
  }
  const requestCacheVersion = assetTypeCacheVersion;

  const params = new URLSearchParams({
    limit: normalizedValues.length
      ? String(Math.max(normalizedValues.length, 10))
      : "10",
    offset: String(offset),
  });
  if (search) {
    params.set("search", search);
  }
  normalizedValues.forEach((value) => params.append("value", value));

  const request = fetch(
    `${apiBase}/logic/workflows/api/asset-types?${params.toString()}`,
    {
      headers: {
        Accept: "application/json",
        ...(window.apiToken
          ? { authorization: `Bearer ${window.apiToken}` }
          : {}),
      },
    }
  )
    .then(async (response) => {
      if (!response.ok) {
        return { options: [], hasMore: false };
      }

      const data = await response.json();
      const options = (data.types || []).map(toAssetTypeOption);
      if (requestCacheVersion === assetTypeCacheVersion) {
        cacheAssetTypeOptions(options);
      }
      return {
        options,
        hasMore: data.hasMore,
      };
    })
    .then((result) => {
      if (requestCacheVersion === assetTypeCacheVersion) {
        assetTypeResponseCache.set(requestKey, result);
      }
      return result;
    })
    .catch(() => ({ options: [], hasMore: false }))
    .finally(() => {
      assetTypeRequestCache.delete(requestKey);
    });

  assetTypeRequestCache.set(requestKey, request);
  return request;
};

export const getAssetKindValueLabels = async (values) => {
  if (!values?.length) {
    return [];
  }

  const missingValues = values.filter(
    (value) => value && !assetTypeOptionCache.has(value)
  );
  if (missingValues.length) {
    await fetchAssetTypeOptions({ values: missingValues });
  }

  return values.map(
    (value) => assetTypeOptionCache.get(value) || toAssetIconOption(value)
  );
};

export const getAssetKindValueLabel = async (value) => {
  const labels = await getAssetKindValueLabels([value]);
  return labels[0];
};
